<template>
  <loader :is-loading="isLoading" color="primary" :full-page="true">
    <v-flex>
      <v-card flat id="employee-card" class="pa-5">
        <v-card-title id="employee-title" class="form-title">
          <span>{{ $t(`components.employeeForm.${mode}.title`) }}</span>
          <v-spacer></v-spacer>
          <v-icon @click="forceClear()">mdi-close</v-icon>
        </v-card-title>
        <v-card-text id="employee-form" class="form-content">
          <v-row>
            <v-col cols="12">
              <span class="text-uppercase input-label">{{ $t(`components.employeeForm.default.name`) }}</span>
            </v-col>
            <v-col cols="6">
              <v-text-field
                :placeholder="$t(`components.employeeForm.default.givenName`)"
                :class="{ 'employee-field-error': $v.employee.givenName.$error }"
                v-model.trim="$v.employee.givenName.$model"
                class="employee-field"
                height="44"
                outlined
                clearable
                hide-details
              >
                <template v-if="$v.employee.givenName.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.employee.givenName"/>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.trim="$v.employee.familyName.$model"
                :class="{ 'employee-field-error': $v.employee.familyName.$error }"
                :placeholder="$t(`components.employeeForm.default.familyName`)"
                class="employee-field"
                outlined
                clearable
                hide-details
                no-filter
              >
                <template v-if="$v.employee.familyName.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.employee.familyName"/>
                </template>
              </v-text-field>
            </v-col>

            <v-col>
              <span class="text-uppercase input-label">{{ $t(`components.employeeForm.default.email`) }}</span>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model.trim="$v.employee.email.$model"
                :placeholder="$t(`components.employeeForm.default.emailPlaceholder`)"
                :class="{ 'employee-field-error': $v.employee.email.$error }"
                class="employee-field email-input"
                outlined
                clearable
                hide-details
                no-filter
              >
                <template v-if="$v.employee.email.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.employee.email"/>
                </template>
              </v-text-field>
            </v-col>

            <v-col>
              <span class="text-uppercase input-label">{{ $t(`components.employeeForm.default.role`) }}</span>
            </v-col>

            <v-col cols="12">
              <v-select
                :disabled="mode === 'edit'"
                :items="roles"
                v-model="employee.role"
                class="employee-field"
                item-text="name"
                item-value="value"
                :menu-props="{ bottom: true, offsetY: true }"
                outlined
                hide-details
              ></v-select>
            </v-col>

            <v-col cols="12" >
              <span class="text-uppercase input-label" v-if="isRolePassenger() || isRoleGuest()">{{ $t('components.employeeForm.default.account') }}</span>
              <span class="text-uppercase input-label" v-if="isRoleCorpoAdmin()">{{ $t('components.employeeForm.default.corporations') }}</span>
            </v-col>

            <v-col cols="12">
              <v-select
                v-if="isRolePassenger() || isRoleGuest()"
                :disabled="mode === 'edit'"
                outlined
                hide-details
                :menu-props="{ bottom: true, offsetY: true }"
                item-text="name"
                v-model="corporation"
                :items="corporations"
                :loading="corporationsLoading"
                class="employee-field"
                return-object
                :placeholder="$t('components.employeeForm.default.accountPlaceholder')"
              />
              <v-autocomplete
                v-if="isRoleCorpoAdmin()"
                outlined
                filled
                hide-details
                icon="$icons_product_corporation"
                item-text="name"
                item-value="id"
                v-model="selectedCorporations"
                :placeholder="$t('components.usersForm.default.corporations')"
                :loading="corporationsLoading"
                :items="corporations"
                :multiple="true"
                :search-input.sync="search"
              >
              </v-autocomplete>
            </v-col>

            <v-col cols="12" v-if="isRolePassenger() || isRoleGuest()">
              <span class="text-uppercase input-label">{{ $t('components.employeeForm.default.group') }}</span>
            </v-col>

            <v-col cols="12" v-if="isRolePassenger() || isRoleGuest()">
              <v-select
                outlined
                hide-details
                :menu-props="{ bottom: true, offsetY: true }"
                item-text="name"
                v-model="group"
                :items="groups"
                :loading="groupsLoading"
                class="employee-field"
                return-object
                :placeholder="$t('components.employeeForm.default.groupPlaceholder')"
              />
            </v-col>

            <v-col v-if="isRolePassenger() || isRoleGuest()">
              <span class="input-label">{{ $t(`components.employeeForm.default.phone`) }}</span>
            </v-col>


            <v-col v-if="isRoleCorpoAdmin()">
              <span class="text-uppercase input-label">{{ $t(`components.employeeForm.default.communicationLanguage`) }}</span>
            </v-col>

            <v-col cols="12" v-if="isRoleCorpoAdmin()">
              <v-select
                :items="languageOptions"
                v-model="employee.communicationLanguage"
                class="language-field"
                :menu-props="{ bottom: true, offsetY: true }"
                item-text="name"
                item-value="value"
                outlined
                hide-details
                background-color="#f8f8f8"
              >
              </v-select>
            </v-col>
            <v-col cols="12" v-if="isRoleCorpoAdmin() && mode !== 'edit'">
              <v-checkbox
                v-model="createPassengerAccount"
                :label="$t(`components.usersForm.default.corporAdminCheckbox`)"
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col v-if="isRolePassenger() || isRoleGuest() || createPassengerAccount" cols="12">
              <vue-tel-input
                :disabled="mode === 'edit'"
                ref="employee-phone-input"
                filled
                mode="national"
                :class="{ 'employee-field-error': $v.phoneNumber.$error }"
                :autoDefaultCountry="false"
                :defaultCountry="countryCode"
                :dropdownOptions="{
                  showDialCodeInList: true,
                  showDialCodeInSelection: true,
                  showFlags: true,
                  width: '240px',
                }"
                :inputOptions="{ showDialCode: false }"
                v-model.trim="phoneNumber"
              >
                <template v-if="$v.phoneNumber.$error && !$v.phoneNumber.required" v-slot:icon-right>
                  <div class="mr-2 mt-2">
                    <error-tooltip :validator="$v.phoneNumber"/>
                  </div>
                </template>
              </vue-tel-input>
            </v-col>

            <v-col>
              <p v-if="employee.givenName && mode !== 'edit'" class="mb-0">
                <span class="font-weight-bold text--black">{{ `${employee.givenName}` }}</span>
                {{ $t(`components.employeeForm.default.confirmation`, { attribute: companyName }) }}
              </p>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="mt-4">
          <v-btn
            class="text-capitalize"
            color="grey darken-1"
            text
            @click="forceClear()"
          >
            {{ $t(`components.employeeForm.${mode}.cancel`) }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            class="text-capitalize white--text"
            color="#333333"
            depressed
            :disabled="validateForm"
            @click="submitDialog"
          >
            {{ $t(`components.employeeForm.${mode}.agree`) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </loader>
</template>

<script>
import { mapGetters } from 'vuex';
import { find, isBoolean } from 'lodash';
import { getRoles } from '@/mixins/authentication.mixins';
import { bookingChannelComputed } from '@/mixins/bookingChannel.mixins';
import { languageMethods, languageList } from '@/mixins/language.mixins';
import { entityLoader } from '@/mixins/entityLoader.mixins';
import employeeValidation from '@/helpers/rules/employeeValidation';
import ErrorTooltip from '@/components/elements/ErrorTooltip.vue';
import { CORP_ADMIN } from '@/helpers/userRoles';

const blankState = {
  givenName: '',
  familyName: '',
  email: null,
  phone: '',
  role: 'passenger',
  group: null,
  communicationLanguage: null,
  bookingChannelId: null,
  roles: [],
};

export default {
  name: 'employee-form',
  mixins: [bookingChannelComputed, getRoles, entityLoader, languageMethods, languageList],
  props: {
    mode: {
      type: String,
      default: 'add',
      required: true,
    },
    dataInfo: {
      type: Object,
      required: false,
    },
  },
  components: {
    ErrorTooltip,
  },
  validations: {
    ...employeeValidation,
  },
  data() {
    return {
      error: null,
      isLoading: false,
      employee: null,
      search: null,
      phoneNumber: null,
      cachedPhoneNumber: null,
      initializeCorporation: true,
      saveLoading: false,
      roles: [
        { name: this.$t('components.employeeForm.default.passenger'), value: 'passenger' },
        { name: this.$t('components.employeeForm.default.guest'), value: 'guest' },
      ],
      groups: [],
      group: null,
      corporationId: null,
      corporations: [],
      corporation: null,
      corporationsLoading: false,
      groupsLoading: false,
      selectedCorporations: [],
      createPassengerAccount: false,
    };
  },
  async mounted() {
    this.corporationsLoading = true;
    const { data } = await this.$store.dispatch('corporations/listCorporations', { maxPerPage: 100, sort: 'name' });
    this.corporations = data ?? [];
    this.resetCorporation();
    this.corporationsLoading = false;
    await this.$store.dispatch('users/getUserRole');
    if (this.getUserRole.name === 'corporationAdmin') {
      this.roles.push({ name: this.$t('components.usersForm.default.corpoAdmin'), value: CORP_ADMIN });
    }
  },
  computed: {
    ...mapGetters('users', ['getUserRole']),
    ...mapGetters('corporations', ['getSelectedCorporationId']),
    ...mapGetters({
      countryCode: 'bookingChannel/defaultCountry',
    }),
    primaryColor() {
      return this.$vuetify.theme.themes.light.primary;
    },
    validateForm() {
      return this.isRolePassenger ? this.$v.employee.$invalid : this.$v.$invalid;
    },
  },
  watch: {
    phoneNumber(val) {
      const entryPhoneLength = val.length;

      if (val) this.$v.phoneNumber.$touch();

      if (this.cachedPhoneNumber && entryPhoneLength > this.cachedPhoneNumber.length) {
        this.phoneNumber = this.cachedPhoneNumber;
        return;
      }

      if (this.isPhoneNumberValid()) {
        this.countryCode = this.$refs['employee-phone-input'].phoneObject.countryCode;
        this.employee.phone = this.$refs['employee-phone-input'].phoneObject.number;
        this.cachedPhoneNumber = this.$refs['employee-phone-input'].phoneObject.number;
      }
    },
    corporation: {
      async handler(value) {
        if (!value) return;

        this.groupsLoading = true;
        const { data } = await this.$store.dispatch('employee/listEmployeeGroups', { maxPerPage: 100, corporationId: value.id });

        this.groups = data ?? [];

        if (this.group) {
          const selectedGroup = this.groups.find((el) => el.id === this.group.id);

          this.group = selectedGroup ? this.group : null;
        }

        this.groupsLoading = false;
      },
      immediate: true,
    },
    employee() {
      this.resetCorporation();
    },
    dataInfo: {
      async handler(val) {
        this.employee = val ? { ...blankState, ...val } : { ...blankState };
        if (this.employee.phone) {
          this.phoneNumber = this.employee.phone;
          this.employee.role = 'passenger';
        } else {
          this.phoneNumber = '';
        }

        if (!this.employee.employeeGroupId) {
          this.group = null;
          return;
        }

        const { data } = await this.$store.dispatch('employee/listEmployeeGroups',
          {
            maxPerPage: 100,
            ids: [val.employeeGroupId],
          });

        [this.group] = data;
      },
      immediate: true,
    },
  },
  created() {
    this.populateLanguagesSelectList();
  },
  methods: {
    resetCorporation() {
      const corporationId = this.isViewInEditContext() ? this.employee?.corporationId : this.getSelectedCorporationId;

      if (corporationId) this.corporation = find(this.corporations, { id: corporationId });
      else this.corporation = null;
    },
    isPhoneNumberValid() {
      if (this.$refs && this.$refs['employee-phone-input'] && this.$refs['employee-phone-input'].phoneObject) {
        return this.$refs['employee-phone-input'].phoneObject.valid;
      }
      return false;
    },
    async postEmployeeCreation() {
      this.employee.corporationId = this.corporation.id;
      this.employee.email = this.employee.email === null ? undefined : this.employee.email;
      const url = this.getStateUrlForSaving();
      const result = await this.$store.dispatch(url, this.employee);
      return result;
    },
    async assignEmploeesToGroup(data) {
      const params = {
        employeeGroupId: this.group.id,
        requestParams: {
          addedEmployeeIds: [isBoolean(data) ? this.employee.id : data.id],
          removedEmployeeIds: [],
        },
      };

      await this.$store.dispatch('employee/updateEmployeeGroupEmployees', params);
    },
    getStateUrlForSaving() {
      if (this.isRoleGuest()) {
        return this.isViewInEditContext() ? 'guest/updateGuest' : 'guest/createGuest';
      } if (this.isRolePassenger()) {
        return this.isViewInEditContext() ? 'employee/updateEmployee' : 'employee/createEmployee';
      } if (this.isRoleCorpoAdmin()) {
        this.employee.corporations = this.selectedCorporations;
        return this.isViewInEditContext() ? 'users/updateUser' : 'users/createUser';
      }
      throw new Error('Could not determine the supported role for the user');
    },
    isRoleGuest() {
      return this.employee.role === 'guest';
    },
    isRolePassenger() {
      return this.employee.role === 'passenger';
    },
    isRoleCorpoAdmin() {
      return this.employee.role === 'corporationAdmin';
    },
    /**
     * Return true if the form is displayed in the "Edit" context - i.e. the user
     * is created and we are trying to modify it.
     * @return {boolean}
     */
    isViewInEditContext() {
      return this.mode === 'edit';
    },
    async submitDialog() {
      this.isLoading = true;
      let result = null;

      if (!this.validateForm) {
        result = await this.postEmployeeCreation();
        if (result && this.isRoleCorpoAdmin() && this.employee.phone) {
          this.employee.role = 'passenger';
          result = await this.postEmployeeCreation();
        }
      }

      if (result) {
        if ((this.employee.group || this.group) && !this.isRoleCorpoAdmin()) this.assignEmploeesToGroup(result);

        this.forceClear(true);
      } else {
        this.isLoading = false;
      }
    },
    forceClear(submitted) {
      if (this.mode === 'add') {
        this.employee = { ...blankState };
        this.phoneNumber = '';
      }

      this.isLoading = false;

      this.$v.$reset();
      this.$emit('closeDialog', submitted);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/phoneInput.scss';

  .input-label {
    font-style: normal;
    font-weight: 400;
    font-size: 10px;
    line-height: 12px;
    display: flex;
    align-items: center;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #888888;
  }

  .employee-field {
    background: #F8F8F8;
    box-sizing: border-box;
    border-radius: 6px;
    font-style: normal;
    line-height: 15px;
    border: 1px solid #EEEEEE;
    letter-spacing: -0.005em;
    color: #333333;
  }

  .employee-field:focus-within {
    border-color: #47D061;
  }

  .employee-field-error {
    border: 1px solid #EB5757 !important;
  }

  .form-content {
    padding-bottom: 4px;
  }

  .loader-flex {
    margin: 0% !important;
    height: 528px;
  }

</style>

<style lang="scss">
.employee-field .vti__dropdown {
  outline: none;
}

.employee-field .v-input__control > .v-input__slot fieldset {
  border: none !important;
}

.v-text-field--outlined > .v-input__control > .v-input__slot {
  min-height: 44px;

  .v-input__prepend-inner, .v-input__append-inner {
    margin-top: 11px
  }
}
</style>
